import { type Context, Controller } from '@hotwired/stimulus';
import {
  EVENT_TASK_INPUT_CHANGED,
  EVENT_TASK_INPUT_FOCUS_REQUEST,
} from 'src/events';
import { createElement, createSpinner } from 'src/utils/dom-create';
import { toggleAttribute, toggleHidden } from 'src/utils/dom-toggle';

const PICKER_ACTIONS = [
  'tasks--resource-picker:pick@window->tasks--ds-input#onPickedResource',
  'tasks--resource-picker:cancel@window->tasks--ds-input#onCancelPickResource',
];

export default class TasksDsInputController extends Controller {
  public static values = {};

  private connected: boolean = false;
  private abortController: AbortController | undefined;

  constructor(context: Context) {
    super(context);

    this.onInput = this.onInput.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onLinkClick = this.onLinkClick.bind(this);
    this.onLinkKeydown = this.onLinkKeydown.bind(this);
  }

  connect(): void {
    this.connected = true;
    this.abortController = new AbortController();

    // Upgrade employee and site boxes
    this.element
      .querySelectorAll(
        [
          'ds-input[ds-type="asset"]',
          'ds-input[ds-type="contractor"]',
          'ds-input[ds-type="employee"]',
          'ds-input[ds-type="project"]',
          'ds-input[ds-type="site"]',
        ].join(','),
      )
      .forEach((dsInput) => {
        const input = dsInput.querySelector('input');
        if (!input) {
          return;
        }

        const type = dsInput.getAttribute('ds-type');

        const replacement = document.createElement('div');
        replacement.setAttribute('data-target', 'replacement');
        replacement.classList.add(
          'flex',
          'flex-wrap',
          'items-center',
          'gap-x-2',
          'gap-y-1.5',
        );

        const button = document.createElement('button');
        button.type = 'button';
        button.classList.add(
          'button',
          '--green',
          'rounded-md!',
          'mt-1',
          'shrink-0',
          'cursor-pointer',
        );

        const messageId = this.messageId;
        const dsName = dsInput.getAttribute('ds-name') ?? '- missing -';

        const actionButtonSelector = [];
        if (messageId) {
          actionButtonSelector.push(`#message-${messageId}`);
        }
        actionButtonSelector.push(`ds-input[ds-name="${dsName}"]`);
        actionButtonSelector.push('button[data-target="pickButton"]');

        // Make the button
        button.classList.add('print:hidden');
        button.textContent = `Pick ${type}`;
        toggleAttribute('disabled', button, dsInput.hasAttribute('disabled'));
        button.setAttribute('data-target', 'pickButton');
        button.setAttribute('data-action', 'tasks--ds-input#onPickResource');
        button.setAttribute(
          'data-tasks--ds-input-message-id-param',
          messageId ?? '',
        );
        button.setAttribute('data-tasks--ds-input-ds-name-param', dsName);
        button.setAttribute(
          'data-tasks--ds-input-ds-type-param',
          dsInput.getAttribute('ds-type') ?? 'unknown',
        );
        button.setAttribute(
          'data-tasks--ds-input-input-name-param',
          input.name ?? '',
        );
        button.setAttribute(
          'data-tasks--ds-input-action-button-selector-param',
          actionButtonSelector.join(' '),
        );

        replacement.append(button);

        input.type = 'hidden';
        input.after(replacement);

        // Remove current value
        const label = input.closest('label');
        if (label) {
          label.classList.add('max-w-full', 'min-w-0');
          const oldCurrent = label.querySelector('[data-target="current"]');
          if (oldCurrent) {
            toggleHidden(oldCurrent, true);
            oldCurrent.setAttribute('data-target', 'old-current');
          }
        }

        const currentValue = document.createElement('div');
        currentValue.setAttribute('data-target', 'current');
        currentValue.classList.add(
          'border',
          'border-dotted',
          'flex',
          'items-center',
          'min-w-0',
          'mt-1',
          'px-2',
          'py-2',
          'pr-3.5',
          'rounded-md',
          'shadow-xs',
          'empty:hidden',
        );
        replacement.append(currentValue);

        // Show current_value
        if (input.value) {
          this.setCurrentValue(input.value, dsName);
        }
      });

    this.element.querySelectorAll('ds-input,ds-toggle').forEach((dsInput) => {
      const requiredInput = dsInput.querySelector('[aria-required="true"]');
      if (!requiredInput) {
        return;
      }

      let label: HTMLElement | null = dsInput.querySelector('fieldset,label');
      if (!label) {
        return;
      }

      const fieldset = label.closest('fieldset.ds-input-group');
      if (fieldset) {
        label = fieldset.querySelector('legend') || label;
      }

      const innerLabel = label.querySelector<HTMLElement>('.form-label');
      if (innerLabel) {
        label = innerLabel;
      }

      if (!label.hasAttribute('data-upgraded')) {
        label.setAttribute('data-upgraded', '');
        label.setAttribute('data-original-label', label.textContent);

        const element = createElement('span', {}, [
          createElement(
            'span',
            {
              'aria-hidden': 'true',
              class: 'text-red-600 dark:text-red-400 inline-block px-0.5',
            },
            '*',
          ),
          createElement('span', { class: 'sr-only' }, '(required)'),
        ]);

        label.append(element);
      }
    });

    this.element.querySelectorAll('ds-input-link').forEach((link) => {
      this.upgradeLink(link);
    });

    this.element.addEventListener('input', this.onInput);
    this.element.addEventListener('focusout', this.onBlur);
  }

  disconnect(): void {
    this.connected = false;
    this.abortController?.abort('TasksDsInputController#disconnect');

    this.element
      .querySelectorAll(
        [
          'ds-input[ds-type="asset"]',
          'ds-input[ds-type="contractor"]',
          'ds-input[ds-type="employee"]',
          'ds-input[ds-type="project"]',
          'ds-input[ds-type="site"]',
        ].join(','),
      )
      .forEach((dsInput) => {
        // Remove replacement
        dsInput.querySelector('[data-target="replacement"]')?.remove();

        // Restore input
        const input = dsInput.querySelector<HTMLInputElement>(
          'input[type="hidden"]',
        );
        if (input) {
          input.type = 'url';
        }

        // Restore old-current
        const oldCurrent = dsInput.querySelector('[data-target="old-current"]');
        if (oldCurrent) {
          toggleHidden(oldCurrent, false);
          oldCurrent.setAttribute('data-target', 'current');
        }
      });

    this.element.removeEventListener('input', this.onInput);
    this.element.removeEventListener('blur', this.onBlur);
  }

  private upgradeLink(element: Element) {
    if (element.hasAttribute('role')) {
      return;
    }

    element.setAttribute('role', 'button');
    element.setAttribute('tabindex', '0');
    element.classList.add('link');
    element.addEventListener('click', this.onLinkClick);
    element.addEventListener('keydown', this.onLinkKeydown);
  }

  private get messageId(): string | undefined {
    return this.element
      .closest('[data-boundary="message"]')
      ?.id?.split('message-')
      ?.pop();
  }

  private findDsInput(dsName: string) {
    if (/[^-_A-z0-9]/.test(dsName)) {
      throw new Error('Invalid ds-name value. Expected [-_A-z0-9]+');
    }

    return this.element.querySelector(`ds-input[ds-name="${dsName}" i]`);
  }

  private getCurrentValue(dsName: string) {
    const dsInput = this.findDsInput(dsName);
    if (!dsInput) {
      throw new Error(`Expected ds-input with ds-name="${dsName}"`);
    }

    const input = dsInput.querySelector<HTMLInputElement>(
      'input[type="hidden"]',
    );
    if (!input) {
      throw new Error('Expected input with type="hidden"');
    }

    return input.value;
  }

  private setCurrentValue(nextValue: string, dsName: string) {
    const dsInput = this.findDsInput(dsName);
    if (!dsInput) {
      return; // ignore, different controller
    }

    const button = dsInput.querySelector<HTMLButtonElement>(
      'button[data-target="pickButton"]',
    );
    if (!button) {
      throw new Error('Expected button with data-target="pickButton"');
    }

    const input = dsInput.querySelector<HTMLInputElement>(
      'input[type="hidden"]',
    );
    if (!input) {
      throw new Error('Expected input with type="hidden"');
    }

    input.value = nextValue;

    const previewer = dsInput.querySelector('[data-target="current"]');
    if (!previewer) {
      console.debug('[ds-input] no previewer', dsInput);
      return;
    }

    if (!input.value) {
      previewer.innerHTML = '';
      return;
    }

    const wasDisabled = button.disabled;
    button.disabled = true;
    previewer.replaceChildren(
      createSpinner('Loading current preview', 'size-4', 'ml-1.5'),
    );

    try {
      const url = new URL(input.value);

      fetch(`${url.pathname}`, {
        headers: {
          accept: 'text/vnd.kaboom.combobox-options, application/problem+json',
        },
        signal: this.abortController?.signal,
      })
        .then((response) => {
          if (response.ok) {
            return response.text();
          }

          throw new Error('Failed to fetch current value');
        })
        .then((html) => {
          if (!this.connected) {
            return;
          }

          const element = document.createElement('div');
          element.innerHTML = html;
          if (element.firstElementChild?.tagName === 'LI') {
            previewer.innerHTML = element.firstElementChild.innerHTML;
          }

          button.disabled = wasDisabled;
        })
        .catch((_) => {
          if (!this.connected) {
            return;
          }

          button.disabled = wasDisabled;
          previewer.innerHTML = '';
        });
    } catch (error) {
      console.error(error);
    }
  }

  public onPickResource(event: Event) {
    if (
      !('params' in event) ||
      typeof event.params !== 'object' ||
      !event.params
    ) {
      throw new Error('Expected custom event with params property');
    }

    const stimulusAction = event as CustomEvent & {
      params: {
        messageId: string;
        dsName: string;
        dsType: 'employee' | 'site';
        inputName: string;
        actionButtonSelector: string;
      };
    };
    if (
      !('dsName' in stimulusAction.params) ||
      !('dsType' in stimulusAction.params) ||
      !('actionButtonSelector' in stimulusAction.params)
    ) {
      throw new Error(
        'Expected dsName, dsType, and actionButtonSelector in params, actual: ' +
          Object.keys(stimulusAction.params).toString(),
      );
    }

    // Add action listener for picking a resource
    const currentElementAction = (
      this.element.getAttribute('data-action') || ''
    ).split(' ');

    this.element.setAttribute(
      'data-action',
      currentElementAction
        .concat(PICKER_ACTIONS)
        .filter(Boolean)
        .filter((value, index, self) => self.indexOf(value) === index)
        .join(' '),
    );

    const detail = {
      ...stimulusAction.params,
      inputValue: this.getCurrentValue(stimulusAction.params.dsName) ?? '',
    };

    console.debug('[ds-input] request resource picking', detail);
    this.dispatch('task/pick-resource', {
      detail,
    });
  }

  public onPickedResource(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const stimulusAction = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        dsName: string;
        value: string;
      };
    };
    if (
      !('dsName' in stimulusAction.detail) ||
      !('value' in stimulusAction.detail)
    ) {
      throw new Error(
        'Expected dsName, value in detail, actual: ' +
          Object.keys(stimulusAction.detail).toString(),
      );
    }

    const { dsName, value } = stimulusAction.detail;

    const dsInput = this.findDsInput(dsName);
    if (!dsInput) {
      // Ignore with warning
      console.warn(
        `[ds-input] called #onPickedResource but ds-name (${dsName}} was not found in controller`,
        this.element,
      );
      return;
    }

    this.setCurrentValue(value, dsName);

    const input = dsInput.querySelector('[aria-invalid]');
    input?.removeAttribute('aria-invalid');

    // Propagate change
    const messageId = this.messageId;
    this.dispatch(EVENT_TASK_INPUT_CHANGED, {
      detail: { messageId, dsName, value, type: 'input' },
    });

    // Remove action listener for picking a resource
    const currentElementAction = (
      this.element.getAttribute('data-action') || ''
    ).split(' ');

    this.element.setAttribute(
      'data-action',
      currentElementAction
        .filter((action) => !PICKER_ACTIONS.includes(action))
        .join(' '),
    );
  }

  public onCancelPickResource(_event: Event) {
    // Remove action listener for picking a resource
    const currentElementAction = (
      this.element.getAttribute('data-action') || ''
    ).split(' ');

    this.element.setAttribute(
      'data-action',
      currentElementAction
        .filter((action) => !PICKER_ACTIONS.includes(action))
        .join(' '),
    );
  }

  public onInput(event: Event) {
    const input = event.target as
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    const dsInput = input.closest('ds-input,ds-toggle');
    if (!dsInput) {
      // Ignore
      return;
    }

    if (dsInput.tagName === 'DS-TOGGLE') {
      return this.onToggle(event, dsInput as HTMLElement);
    }

    const type = dsInput.getAttribute('ds-type') || '__unknown';

    const changeType = ['label', 'labels'].includes(type) ? 'label' : 'input';

    if (
      [
        'radio',
        'checkbox',
        'label',
        'labels',
        'button',
        'switch',
        'choice',
        'toggle',
      ].includes(type)
    ) {
      let value: string | string[] = input.value;

      if (type === 'labels' || type === 'checkbox') {
        const values: string[] = [];
        this.element
          .querySelectorAll<HTMLInputElement>(
            `input[type='checkbox'][name="${input.name}"]:checked,input[type="hidden"][name="${input.name}"]`,
          )
          .forEach((input) => values.push(input.value));

        value = values;
      }

      if (input.ariaReadOnly) {
        // Revert if it was readonly
        if (type === 'checkbox' || type === 'button') {
          (input as HTMLInputElement).checked = !(input as HTMLInputElement)
            .checked;
        }
      } else {
        const messageId = this.messageId;
        this.dispatch(EVENT_TASK_INPUT_CHANGED, {
          detail: {
            messageId,
            dsName: dsInput.getAttribute('ds-name'),
            value,
            type: changeType,
          },
        });
      }

      input.removeAttribute('aria-invalid');
      input.closest('fieldset')?.removeAttribute('aria-invalid');

      if (type === 'button') {
        input.ariaReadOnly = 'true';
      }
    } else {
      dsInput.setAttribute('data-dirty', '1');
    }
  }

  private onToggle(_event: Event, dsToggle: HTMLElement) {
    const input = dsToggle.querySelector<HTMLInputElement>(
      'input[type="checkbox"]',
    );
    if (!input) {
      return;
    }

    const value = input.checked ? input.value : '';
    input.disabled = true;

    const messageId = this.messageId;
    this.dispatch(EVENT_TASK_INPUT_CHANGED, {
      detail: {
        messageId,
        dsName: dsToggle.getAttribute('ds-name'),
        value,
        type: 'toggle',
      },
    });
  }

  public onBlur(event: Event) {
    const input = event.target as
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    const dsInput = input.closest('ds-input');
    if (!dsInput) {
      // Ignore
      return;
    }

    // Optimization to ignore inputs that have not changed
    if (!dsInput.hasAttribute('data-dirty')) {
      return;
    }

    const type = dsInput.getAttribute('ds-type') || '__unknown';
    const messageId = this.messageId;

    dsInput.removeAttribute('data-dirty');
    input.removeAttribute('aria-invalid');

    if (input.value) {
      if (input.reportValidity()) {
        this.dispatch(EVENT_TASK_INPUT_CHANGED, {
          detail: {
            messageId,
            dsName: dsInput.getAttribute('ds-name'),
            value: input.value,
            type: ['label', 'labels'].includes(type) ? 'label' : 'input',
          },
        });
      } else {
        input.setAttribute('aria-invalid', 'true');
      }
    }
  }

  private onLinkClick(event: Event) {
    if (!event.target || !(event.target instanceof Element)) {
      return;
    }

    const target = event.target.closest('ds-input-link');
    if (!target) {
      return;
    }

    const dsName =
      target.getAttribute('ds-input') || target.getAttribute('data-input');
    if (!dsName) {
      return;
    }

    this.dispatch(EVENT_TASK_INPUT_FOCUS_REQUEST, { detail: { dsName } });
  }

  private onLinkKeydown(event: Event) {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      this.onLinkClick(event);
    }
  }
}
